import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { addDays, format } from 'date-fns';
import {
  animationFrames,
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { startWith, take } from 'rxjs/operators';
import { utcDifferenceInDays } from 'server/helpers/utcDifferenceInDays';
import { Users } from 'server/shared/collections/users';
import { CalendarTableHelper } from 'server/shared/helpers/calendarTableHelper';
import {
  deleteEmploymentAvailability,
  updateEmploymentAvailability,
} from 'server/shared/helpers/employmentAvailabilityHelper';
import { formatUserName } from 'server/shared/helpers/user/formatUserName';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import {
  DispoUser,
  EmploymentAvailability,
  EmploymentAvailabilityStatusEnum,
  PoolerUser,
  UnavailabilityReasonEnum,
  User,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';
import { SubscriptionService } from 'src/app/shared/subscription.service';
import { CustomerService } from '../../../services/customer.service';
import { PoolerService } from '../../../services/pooler/pooler.service';
import {
  EmploymentAvailabilityEditDialogComponent,
  EmploymentAvailabilityEditDialogData,
} from '../../availability-edit/availability-edit-dialog/availability-edit-dialog.component';
import { CreateEmploymentDialogComponent } from '../../availability-edit/create-employment-dialog/create-employment-dialog.component';
import {
  EmploymentAvailabilityWorkingJobDialogComponent,
  EmploymentAvailabilityWorkingJobDialogData,
} from '../../availability-edit/employment-availability-working-job-dialog/employment-availability-working-job-dialog';
import {
  EmploymentAvailabilityListUser,
  EmploymentAvailabilityManagementService,
} from '../service/employment-availability-management.service';

export interface EmploymentAvailabilitiesWithLength
  extends EmploymentAvailability {
  daysBetween: number;
  daysOffset: number;
  daysVisibleStart: number;
  daysVisibleEnd: number;
  calendarStart: string;
  calendarEnd: string;
}
@Component({
  selector: 'app-temp-work-availabilities-user-list',
  templateUrl: './employment-availabilities-user-list.component.html',
  styleUrls: [
    // first load the shared styles to allow overwriting with same specificity
    './custom-availability-table/availability-table-shared-styles.scss',
    './employment-availabilities-user-list.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentAvailabilitiesUserListComponent
  implements OnInit, OnDestroy
{
  loggedInUser$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  @Input() set loggedInUser(value: DispoUser | undefined) {
    this.loggedInUser$.next(value);
  }
  get loggedInUser() {
    return this.loggedInUser$.value;
  }

  displayListItemsCount = 20;

  availabilitiesCount$ = this.managementService.calendarRange$.pipe(
    map(({ from, to }) => {
      return Array.from({ length: utcDifferenceInDays(to, from) + 1 }, (_, i) =>
        format(addDays(from, i), dbDayDateToken),
      );
    }),
  );

  availabilityDays$ = this.managementService.calendarRange$.pipe(
    map(({ from, to }) => {
      return Array.from({ length: utcDifferenceInDays(to, from) + 1 }, (_, i) =>
        format(addDays(from, i), dbDayDateToken),
      );
    }),
    shareReplay(1),
  );
  selectedStart$ = this.managementService.fromParam$;
  selectedDatesStartIndex$ = combineLatest([
    this.selectedStart$,
    this.availabilityDays$,
  ]).pipe(
    map(([selectedFrom, availabilityDays]) => {
      const index = availabilityDays.findIndex((day) => day === selectedFrom);
      return index === -1 ? undefined : index;
    }),
    shareReplay(1),
  );

  selectedEnd$ = this.managementService.toParam$;
  selectedDatesEndIndex$ = combineLatest([
    this.selectedEnd$,
    this.availabilityDays$,
  ]).pipe(
    map(([selectedTo, availabilityDays]) => {
      const index = availabilityDays.findIndex((day) => day === selectedTo);
      return index === -1 ? undefined : index;
    }),
  );
  statusEnum = EmploymentAvailabilityStatusEnum;

  private destroyed$ = new Subject<void>();

  preparedUserById = this.managementService.preparedUserById;

  calendarTableHelper$ = this.managementService.calendarRange$.pipe(
    map(({ from, to }) => {
      return new CalendarTableHelper(from, to);
    }),
    shareReplay(1),
  );

  renderdUserIds$ = this.managementService.usersLoader$.pipe(
    map(({ renderedUserIds }) => renderedUserIds),
    shareReplay(1),
  );
  renderedPreparedUsers$ = this.renderdUserIds$.pipe(
    map((renderedUserIds) => {
      return new Map(
        Array.from(renderedUserIds).map(([id, _]) => [
          id,
          this.preparedUserById.get(id),
        ]),
      );
    }),
    shareReplay(1),
  );

  loading$ = combineLatest([
    this.managementService.loadingUsers$,
    this.managementService.usersLoader$.pipe(map((loader) => loader.loading)),
    this.subscriptionService.employmentUsersReady$.pipe(startWith(false)),
  ]).pipe(
    map(
      ([loadingUsers, preparingUsers, employmentUsersReady]) =>
        loadingUsers || preparingUsers || !employmentUsersReady,
    ),
    startWith(true),
  );

  renderedUserIds$ = this.managementService.usersLoader$.pipe(
    map(({ renderedUserIds }) => renderedUserIds),
    shareReplay(1),
  );

  preparedUsers$ = this.managementService.usersLoader$.pipe(
    map(({ users }) => {
      // Return users in the same order as input, but use prepared versions
      return users
        .map((u: PoolerUser) => this.preparedUserById.get(u._id))
        .filter((u): u is EmploymentAvailabilityListUser => u !== undefined);
    }),
    shareReplay(1),
  );

  restoreScrollPositionEffect$ = this.managementService.usersLoader$.pipe(
    filter((loader) => !loader.loading),
    switchMap(() => animationFrames().pipe(take(1))),
    map(() => {
      this.restoreScrollPosition();
    }),
  );

  poolerCustomerMap?: PoolerCustomerMap;

  dispoCustomer?: Customer;

  organisationMap$ = combineLatest([
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([customer]) => {
      return new Map<string, boolean>(
        customer?.organisationIds?.map((organisation) => [organisation, true]),
      );
    }),
  );

  timeout?: NodeJS.Timeout;

  today = format(new Date(), dbDayDateToken);

  lastHorizontalScrollPosition = 0;

  @ViewChild('headerScroll') headerScrollElement?: ElementRef<HTMLDivElement>;
  @ViewChild('cdkScroll') cdkScrollElement?: CdkVirtualScrollViewport;

  hoveredMiniAbsence$ = new BehaviorSubject<string | undefined>(undefined);
  clickPositionInAvailability$ = new BehaviorSubject<
    { x: number; y: number } | undefined
  >(undefined);

  constructor(
    private ngZone: NgZone,
    private poolerService: PoolerService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    public managementService: EmploymentAvailabilityManagementService,
    private customerService: CustomerService,
    private subscriptionService: SubscriptionService,
  ) {
    this.restoreScrollPositionEffect$.subscribe();
    this.poolerService.employmentPoolerCustomerMap$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((poolerMap) => {
        this.poolerCustomerMap = poolerMap;
        this.ngZone.run(() => {
          this.prepareAvailabilityUsers().then();
        });
      });
  }

  async ngOnInit(): Promise<void> {
    this.dispoCustomer = await this.customerService.readyDispoCustomer;
  }
  async prepareAvailabilityUsers() {}

  toggleDetails(user: User) {
    this.ngZone.run(() => {
      user.detailsCollapsed = !user.detailsCollapsed;
    });
  }

  editAvailability(userId: string, availabilityId: string): void {
    const user = Users.findOne({
      _id: userId,
      role: RoleEnum.POOL,
    }) as PoolerUser;
    let availability = user?.employmentAvailabilities?.find(
      (availability) => availability.id === availabilityId,
    );
    if (!availability) return;
    if (!this.dispoCustomer?.pool.activeUserIds.includes(userId)) {
      return;
    }

    if (
      availability.status === EmploymentAvailabilityStatusEnum.WORKING &&
      availability.bookedEmployment
    ) {
      const availabilityData: EmploymentAvailabilityWorkingJobDialogData = {
        availabilityId: availabilityId,
        startDate: availability.startDate,
        endDate: availability.endDate,
        personName: user.firstName + ' ' + user.lastName,
        organisationName: availability.bookedEmployment.organisation.name,
        departmentName: availability.bookedEmployment.department.name,
        unavailabilities: availability.bookedEmployment.unavailabilities,
      };
      this.openWorkingEmploymentDialog(availabilityData);
    } else {
      const availabilityData: EmploymentAvailabilityEditDialogData = {
        personName: user.firstName + ' ' + user.lastName,
        availabilityId: availabilityId,
        startDate: availability.startDate,
        endDate: availability.endDate,
        notes: availability.notes,
        status: availability.status,
        unavailableReason: availability.unavailableReason,
        workload: availability.workload,
      };
      this.openAvailabilityDialog(userId, availabilityData);
    }
  }

  openCreateEmploymentDialog(
    user: PoolerUser,
    availability: EmploymentAvailability,
  ): void {
    const dialogRef = this.dialog.open(CreateEmploymentDialogComponent, {
      data: {
        personName: user.firstName + ' ' + user.lastName,
        departmentId: this.route.snapshot.queryParams.department,
        startDate:
          this.route.snapshot.queryParams.selectedFrom ||
          availability.startDate,
        endDate:
          this.route.snapshot.queryParams.selectedTo || availability.endDate,
        workload: availability.workload || 100,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe(
        (data?: {
          departmentId: string;
          startDate?: string;
          endDate?: string;
          workload?: number;
        }) => {
          if (data) {
            this.router
              .navigate(['temp-work/employments', 'new'], {
                queryParams: {
                  startDate: data.startDate,
                  endDate: data.endDate,
                  departmentId: data.departmentId,
                  poolerId: user._id,
                  workload: data.workload,
                },
              })
              .then();
          }
        },
      );
  }

  openAvailabilityDialog(
    userId: string,
    availabilityData: EmploymentAvailabilityEditDialogData,
  ): void {
    const user = Users.findOne({
      _id: userId,
      role: RoleEnum.POOL,
    }) as PoolerUser;
    const dialogRef = this.dialog.open(
      EmploymentAvailabilityEditDialogComponent,
      {
        data: availabilityData,
      },
    );

    dialogRef
      .afterClosed()
      .subscribe(
        async (result: {
          id?: string;
          startDate: string;
          endDate: string;
          status: EmploymentAvailabilityStatusEnum;
          unavailableReason?: UnavailabilityReasonEnum;
          workload?: number;
          notes?: string;
          toDelete?: boolean;
        }) => {
          if (
            result?.id &&
            user &&
            this.dispoCustomer?.pool.activeUserIds.includes(user._id)
          ) {
            if (result.toDelete) {
              const { employmentAvailabilities, employmentAvailabilityDays } =
                deleteEmploymentAvailability(
                  result.id,
                  user.employmentAvailabilities || [],
                  user.employmentAvailabilityDays || [],
                );
              user.employmentAvailabilities = employmentAvailabilities;
              user.employmentAvailabilityDays = employmentAvailabilityDays;
            } else {
              const { employmentAvailabilities, employmentAvailabilityDays } =
                updateEmploymentAvailability(
                  result.id,
                  result as EmploymentAvailability,
                  user.employmentAvailabilities || [],
                  user.employmentAvailabilityDays || [],
                );
              user.employmentAvailabilities = employmentAvailabilities;
              user.employmentAvailabilityDays = employmentAvailabilityDays;
            }
            this.managementService.updateAvailability(user);
          }
        },
      );
  }

  openWorkingEmploymentDialog(
    availabilityData: EmploymentAvailabilityWorkingJobDialogData,
  ) {
    const dialogRef = this.dialog.open(
      EmploymentAvailabilityWorkingJobDialogComponent,
      {
        data: availabilityData,
      },
    );
  }

  trackByUserId(index: number, user: PoolerUser): string {
    return user._id;
  }

  daySelected(selectionDate: string) {
    combineLatest([
      this.selectedDatesStartIndex$,
      this.selectedDatesEndIndex$,
      this.availabilityDays$,
    ])
      .pipe(take(1))
      .subscribe(
        ([
          selectedDatesStartIndex,
          selectedDatesEndIndex,
          availabilityDays,
        ]) => {
          const rangeIsComplete =
            selectedDatesEndIndex !== undefined &&
            selectedDatesStartIndex !== selectedDatesEndIndex;
          const hasNoRangeYet = selectedDatesStartIndex === undefined;
          const currentStartDate = selectedDatesStartIndex
            ? availabilityDays[selectedDatesStartIndex]
            : undefined;
          const currentEndDate = selectedDatesEndIndex
            ? availabilityDays[selectedDatesEndIndex]
            : undefined;
          const dateIsBeforeRangeStart =
            currentStartDate && selectionDate < currentStartDate;

          const dateIsInCurrentRange =
            currentStartDate &&
            currentEndDate &&
            selectionDate >= currentStartDate &&
            selectionDate <= currentEndDate;

          if (dateIsInCurrentRange) {
            // reset the range
            selectedDatesEndIndex = undefined;
            selectedDatesStartIndex = undefined;
          } else if (rangeIsComplete || hasNoRangeYet) {
            // start new range
            selectedDatesStartIndex = availabilityDays.findIndex(
              (day, index) => {
                return day === selectionDate;
              },
            );
            selectedDatesEndIndex = selectedDatesStartIndex;
          } else if (dateIsBeforeRangeStart) {
            // finish the range but reverse start & end
            selectedDatesEndIndex = selectedDatesStartIndex;
            selectedDatesStartIndex = availabilityDays.findIndex(
              (day, index) => {
                return day === selectionDate;
              },
            );
          } else {
            // finish the range with end date
            selectedDatesEndIndex = availabilityDays.findIndex((day, index) => {
              return day === selectionDate;
            });
          }
          if (
            selectedDatesStartIndex === undefined &&
            selectedDatesEndIndex === undefined
          ) {
            // reset dates
            this.managementService.updateQueryParams({
              selectedFrom: null,
              selectedTo: null,
            });
          } else if (
            selectedDatesStartIndex !== undefined &&
            selectedDatesEndIndex !== undefined
          ) {
            // set dates
            this.managementService.updateQueryParams({
              selectedFrom: availabilityDays[selectedDatesStartIndex],
              selectedTo: availabilityDays[selectedDatesEndIndex],
            });
          }
        },
      );
  }

  updateHeaderScrollPosition($event: Event) {
    const target = $event.target as HTMLDivElement;
    this.setHeaderScrollPosition(target.scrollLeft);
  }

  setHeaderScrollPosition(offset: number) {
    this.lastHorizontalScrollPosition = offset;
    if (this.headerScrollElement) {
      this.headerScrollElement.nativeElement.scrollLeft = offset;
    }
  }

  setListScrollPosition(offset: number) {
    if (this.cdkScrollElement) {
      this.cdkScrollElement.elementRef.nativeElement.scrollLeft = offset;
    }
  }

  restoreScrollPosition() {
    this.setHeaderScrollPosition(this.lastHorizontalScrollPosition);
    this.setListScrollPosition(this.lastHorizontalScrollPosition);
  }

  onAvailabilityClick(
    user: PoolerUser,
    availability: EmploymentAvailability,
    menuTrigger: MatMenuTrigger,
    event?: MouseEvent,
  ) {
    if (event && event.offsetX && event.offsetY) {
      this.clickPositionInAvailability$.next({
        x: event.offsetX,
        y: event.offsetY,
      });
    }
    const canEditAvailability = this.dispoCustomer?.pool.activeUserIds.includes(
      user._id,
    );
    if (canEditAvailability) {
      if (availability.status === EmploymentAvailabilityStatusEnum.AVAILABLE) {
        menuTrigger.openMenu();
      } else {
        this.editAvailability(user._id, availability.id);
      }
    } else if (
      availability.status === EmploymentAvailabilityStatusEnum.AVAILABLE
    ) {
      this.openCreateEmploymentDialog(user, availability);
    }
  }

  formatUserName(user: PoolerUser): string {
    return formatUserName(user, this.dispoCustomer);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
